import * as React from 'react';
import { Text } from '../Text';
import { st, classes } from './FileUpload.st.css';
import { Fieldset } from '../internal/Fieldset';
import { DATA_HOOKS } from './dataHooks';
import { Button } from '../Button';
import FileUploadPopover from './components/FileUploadPopover';
import { Add } from '@wix/wix-ui-icons-common';
import File from './components/File';
import { getAriaDescribedBy } from '../../common/a11y';
import uniqueId from '../../common/uniqueId';
export const FileUpload = ({ accept, label, errorMessage, files, multiple = false, buttonText, explanationText, onChange, onDeleteClick, deleteText, className, disabled, lang, ...rest }) => {
    const fileInputRef = React.useRef(null);
    const clickHandler = React.useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const buttonRef = React.useRef(null);
    const [checkErrorFocus, setCheckErrorFocus] = React.useState(false);
    const [textBelowButtonRef, setTextBelowButtonRef] = React.useState(null);
    const fileHasError = Boolean(files?.find((file) => file.state === 'error'));
    const hasError = Boolean(errorMessage || fileHasError);
    const filesExist = files && Boolean(files.length);
    const isOneFile = filesExist && files.length === 1;
    const handleDeleteClick = (i) => {
        const fileToDelete = files[i].file;
        if (fileInputRef.current &&
            `${fileInputRef.current.value}`.includes(fileToDelete.name)) {
            fileInputRef.current.value = '';
        }
        onDeleteClick?.(i);
    };
    const generateFiles = () => {
        if (isOneFile) {
            return (React.createElement("div", { className: classes.oneFile, ref: setTextBelowButtonRef, tabIndex: 0, "aria-describedby": uniqueErrorId },
                React.createElement(File, { file: files[0], onDeleteClick: () => handleDeleteClick(0), deleteText: deleteText, dataHook: DATA_HOOKS.singleFile })));
        }
        return (React.createElement(FileUploadPopover, { files: files, onDeleteClick: handleDeleteClick, deleteText: deleteText, explanationText: explanationText, hasError: hasError, setTextBelowButtonRef: setTextBelowButtonRef }));
    };
    const generateExplanation = () => {
        if (!explanationText) {
            return undefined;
        }
        return (React.createElement(Text, { "data-hook": DATA_HOOKS.explanation, className: classes.explanation }, explanationText));
    };
    const getErrorMessage = () => {
        if (hasError && isOneFile) {
            return files[0].errorMessage || errorMessage;
        }
        return errorMessage;
    };
    const uniqueErrorId = uniqueId('error-message');
    const ariaDescribedBy = getAriaDescribedBy([
        rest['aria-describedby'],
        hasError ? uniqueErrorId : '',
    ]);
    const handleFileChange = (_files) => {
        setTextBelowButtonRef(null);
        onChange(_files);
        setCheckErrorFocus(true);
    };
    React.useEffect(() => {
        if (checkErrorFocus && filesExist && fileHasError && textBelowButtonRef) {
            textBelowButtonRef.focus();
            setCheckErrorFocus(false);
        }
    }, [checkErrorFocus, filesExist, fileHasError, textBelowButtonRef]);
    return (React.createElement(Fieldset, { "data-error": hasError, dataHook: rest['data-hook'], className: st(classes.root, className), label: label, errorMessage: getErrorMessage(), errorMessageId: uniqueErrorId, lang: lang },
        React.createElement("div", { className: classes.mainContainer },
            React.createElement("input", { className: classes.input, ref: fileInputRef, type: "file", accept: accept, onChange: (e) => handleFileChange(e.currentTarget.files), multiple: multiple }),
            React.createElement(Button, { className: classes.button, onClick: clickHandler, disabled: disabled, "aria-describedby": ariaDescribedBy, "aria-label": rest['aria-label'], upgrade: true, ref: buttonRef, prefixIcon: React.createElement(Add, null) }, buttonText),
            filesExist ? generateFiles() : generateExplanation())));
};
FileUpload.displayName = 'FileUpload';
FileUpload.defaultProps = {
    multiple: false,
};
//# sourceMappingURL=FileUpload.js.map